/**
 * Yes/No markets — "Will Larson finish in the top 10?", "Will Scheffler make the cut?".
 *
 * These are NOT a new question type. Each one is an ordinary player over/under on a stat, with
 * the line placed so that one side of it means "yes":
 *
 *  - `binary`  — a 1/0 stat (made the cut, set the fastest lap). The line is 0.5; YES is over.
 *  - `top_n`   — a lower-is-better position. "Top N" is position under N + 0.5; YES is under.
 *  - `reach`   — a count that must reach N ("the fight reaches round 3"). Over N - 0.5; YES is over.
 *
 * Reusing over/under means the existing grader, the absence handling in it, the stored option
 * shape and the frontend card all settle these unchanged. What changes is only the WORDING: the
 * Coach asks for N rather than a line, the question reads as a yes/no sentence, and the card
 * labels its two buttons Yes and No (`questionMetadata.yesNo`).
 */

export type YesNoMarketKind = 'binary' | 'top_n' | 'reach';

type Language = 'en' | 'fr';

export interface YesNoMarket {
  kind: YesNoMarketKind;
  /** The question for a subject, with N already resolved (1 for `binary`). */
  question: (name: string, n: number, language: Language) => string;
  /** What the Coach asks the operator for in place of a line. Absent for `binary`. */
  nLabel?: string;
  /**
   * Also a real two-competitor pick. Most 1/0 stats are not ("who has the most fastest laps?"
   * among a field that mostly has 0), but exactly one of a match's two players wins a set.
   */
  alsoAPick?: true;
}

const finishTopN =
  (contestEn: string, contestFr: string) =>
  (name: string, n: number, language: Language): string => {
    if (language === 'fr') {
      return n === 1
        ? `Est-ce que ${name} remportera ${contestFr}?`
        : `Est-ce que ${name} terminera dans le top ${n}?`;
    }
    return n === 1 ? `Will ${name} win the ${contestEn}?` : `Will ${name} finish in the top ${n}?`;
  };

const binary = (en: (name: string) => string, fr: (name: string) => string): YesNoMarket => ({
  kind: 'binary',
  question: (name, _n, language) => (language === 'fr' ? fr(name) : en(name)),
});

const SET_ORDINALS = [
  ['1st', '1er'],
  ['2nd', '2e'],
  ['3rd', '3e'],
  ['4th', '4e'],
  ['5th', '5e'],
] as const;

/** Tennis: "Will Mertens win the 1st set?" for each set a best-of-five can have. */
const TENNIS_SET_WINNER_MARKETS: Record<string, YesNoMarket> = Object.fromEntries(
  SET_ORDINALS.map(([en, fr], i) => [
    `tennis_set_${i + 1}_won`,
    {
      ...binary(
        (name) => `Will ${name} win the ${en} set?`,
        (name) => `Est-ce que ${name} remportera le ${fr} set?`
      ),
      alsoAPick: true,
    },
  ])
);

/**
 * Keyed by stat code. Codes are sport-prefixed everywhere except golf, whose `position` is
 * the only stat of that name on the platform.
 */
export const YES_NO_MARKETS: Readonly<Record<string, YesNoMarket>> = {
  // NASCAR
  nascar_finish_position: {
    kind: 'top_n',
    nLabel: 'Top N finish',
    question: finishTopN('race', 'la course'),
  },
  nascar_start_position: {
    kind: 'top_n',
    nLabel: 'Top N start',
    question: (name, n, language) => {
      if (language === 'fr') {
        return n === 1
          ? `Est-ce que ${name} partira en pole position?`
          : `Est-ce que ${name} partira dans le top ${n}?`;
      }
      return n === 1 ? `Will ${name} start on pole?` : `Will ${name} start in the top ${n}?`;
    },
  },
  nascar_fastest_lap: binary(
    (name) => `Will ${name} set the fastest lap?`,
    (name) => `Est-ce que ${name} réalisera le meilleur tour?`
  ),
  nascar_stage_wins: binary(
    (name) => `Will ${name} win a stage?`,
    (name) => `Est-ce que ${name} remportera une étape?`
  ),
  nascar_finished_race: binary(
    (name) => `Will ${name} finish the race?`,
    (name) => `Est-ce que ${name} terminera la course?`
  ),

  // Golf
  position: {
    kind: 'top_n',
    nLabel: 'Top N finish',
    question: finishTopN('tournament', 'le tournoi'),
  },
  made_cut: binary(
    (name) => `Will ${name} make the cut?`,
    (name) => `Est-ce que ${name} passera le cut?`
  ),

  // MMA
  mma_win_by_ko: binary(
    (name) => `Will ${name} win by KO/TKO/DQ?`,
    (name) => `Est-ce que ${name} gagnera par KO/TKO/DQ?`
  ),
  mma_win_by_submission: binary(
    (name) => `Will ${name} win by submission?`,
    (name) => `Est-ce que ${name} gagnera par soumission?`
  ),
  mma_win_by_decision: binary(
    (name) => `Will ${name} win by decision?`,
    (name) => `Est-ce que ${name} gagnera par décision?`
  ),
  mma_goes_distance: binary(
    (name) => `Will ${name}'s fight go the distance?`,
    (name) => `Est-ce que le combat de ${name} ira jusqu'à la limite?`
  ),
  mma_final_round: {
    kind: 'reach',
    nLabel: 'Round reached',
    question: (name, n, language) =>
      language === 'fr'
        ? `Est-ce que le combat de ${name} atteindra le round ${n}?`
        : `Will ${name}'s fight reach round ${n}?`,
  },

  // Tennis
  ...TENNIS_SET_WINNER_MARKETS,
  // Match-level: both players carry the same figure, so the question names the match through one.
  tennis_match_tiebreak: binary(
    (name) => `Will there be a tie-break in ${name}'s match?`,
    (name) => `Y aura-t-il un tie-break dans le match de ${name}?`
  ),
};

export function yesNoMarketFor(statCode: string | null | undefined): YesNoMarket | undefined {
  return statCode ? YES_NO_MARKETS[statCode] : undefined;
}

/**
 * A stat that is final the moment it is recorded: a completed set's winner. A retirement later in
 * the match cannot change it, so a market on it settles rather than being voided with the rest.
 */
export function isFinalOnceRecordedStat(statCode: string | null | undefined): boolean {
  return /^tennis_set_\d+_won$/.test(statCode ?? '');
}

/** A 1/0 stat: meaningless to rank players on, so it is offered as a yes/no only. */
export function isBinaryStat(statCode: string | null | undefined): boolean {
  return yesNoMarketFor(statCode)?.kind === 'binary';
}

/** The over/under line that asks the market's question for N. */
export function yesNoMarginFor(market: YesNoMarket, n: number): number {
  switch (market.kind) {
    case 'binary':
      return 0.5;
    case 'top_n':
      return n + 0.5;
    case 'reach':
      return n - 0.5;
  }
}

/** The N a stored line asks about — the inverse of `yesNoMarginFor`. */
export function yesNoNFor(market: YesNoMarket, margin: number): number {
  switch (market.kind) {
    case 'binary':
      return 1;
    case 'top_n':
      return Math.round(margin - 0.5);
    case 'reach':
      return Math.round(margin + 0.5);
  }
}

/** Which side of the line is "yes": under for a position, over for everything else. */
export function yesComparisonFor(market: YesNoMarket): 'over' | 'under' {
  return market.kind === 'top_n' ? 'under' : 'over';
}

/** The smallest N the Coach accepts — a fight is always in round 1, so "reach" starts at 2. */
export function minimumNFor(market: YesNoMarket): number {
  return market.kind === 'reach' ? 2 : 1;
}
